import React, { useRef } from 'react';
import { Card, Form, Button, Spinner } from 'react-bootstrap';
import { useUploadFile } from '../../../../api/fileUpload/fileUpload.hooks';

const GallerySection = ({ data, onUpdate, onAlert }) => {
  const safeData = data || { isVisible: false, heading: '', pictureUrls: [] };
  const { upload, isLoading: isUploading } = useUploadFile();
  const [uploadProgress, setUploadProgress] = React.useState({ current: 0, total: 0 });
  const fileInputRef = useRef(null);

  const handleToggle = (e) => {
    onUpdate({ ...safeData, isVisible: e.target.checked });
  };

  const handleHeadingChange = (e) => {
    onUpdate({ ...safeData, heading: e.target.value });
  };

  const handleFilesSelect = async (e) => {
    const files = e.target.files ? [...e.target.files] : [];
    if (files.length === 0) return;

    const urls = Array.isArray(safeData.pictureUrls) ? [...safeData.pictureUrls] : [];
    setUploadProgress({ current: 0, total: files.length });

    for (let i = 0; i < files.length; i++) {
      setUploadProgress((prev) => ({ ...prev, current: i + 1 }));
      try {
        const result = await upload(files[i]);
        if (result?.url) {
          urls.push(result.url);
          onUpdate({ ...safeData, pictureUrls: [...urls] });
        }
      } catch (err) {
        console.error('Gallery image upload failed:', err);
        if (onAlert) onAlert(`Failed to upload "${files[i].name}".`, 'danger');
      }
    }

    setUploadProgress({ current: 0, total: 0 });
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (urls.length > 0 && onAlert) onAlert('Images uploaded.', 'success');
  };

  const handleRemoveImage = (index) => {
    const pictureUrls = [...(safeData.pictureUrls || [])];
    pictureUrls.splice(index, 1);
    onUpdate({ ...safeData, pictureUrls });
  };

  const pictureUrls = Array.isArray(safeData.pictureUrls) ? safeData.pictureUrls : [];
  const isUploadingMultiple = uploadProgress.total > 0;

  return (
    <Card className="mb-4 shadow">
      <Card.Header className="bg-neutral-gray text-dark d-flex justify-content-between align-items-center">
        <h5 className="mb-0 text-dark">Gallery Section</h5>
        <Form.Check
          type="checkbox"
          id="gallery-visible"
          label="Visible"
          checked={!!safeData.isVisible}
          onChange={handleToggle}
          className="mb-0"
        />
      </Card.Header>
      <Card.Body>
        <Form.Group className="mb-3">
          <Form.Label>Gallery heading</Form.Label>
          <Form.Control
            type="text"
            value={safeData.heading || ''}
            onChange={handleHeadingChange}
            placeholder="Enter gallery heading"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Upload images</Form.Label>
          <Form.Control
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFilesSelect}
            disabled={isUploading}
          />
          {(isUploading || isUploadingMultiple) && (
            <div className="mt-2 d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span className="text-muted">
                {isUploadingMultiple
                  ? `Uploading ${uploadProgress.current} of ${uploadProgress.total}...`
                  : 'Uploading...'}
              </span>
            </div>
          )}
        </Form.Group>

        {pictureUrls.length > 0 && (
          <>
            <Form.Label className="d-block">Preview</Form.Label>
            <div
              className="d-flex flex-wrap gap-3 mb-0"
              style={{ minHeight: '80px' }}
            >
              {pictureUrls.map((url, index) => (
                <div
                  key={`${url}-${index}`}
                  className="position-relative"
                  style={{ width: '100px', height: '100px' }}
                >
                  <img
                    src={url}
                    alt={`Gallery ${index + 1}`}
                    className="rounded border object-fit-cover w-100 h-100"
                    style={{ objectFit: 'cover' }}
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    className="position-absolute top-0 end-0 translate-middle"
                    style={{ padding: '2px 6px', lineHeight: 1 }}
                    onClick={() => handleRemoveImage(index)}
                    type="button"
                    title="Remove"
                  >
                    <i className="bi bi-x-lg" />
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default GallerySection;
